"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const facilities = [
{
  id: "tennis",
  name: "Tennis Courts",
  tag: "Championship Grade",
  description: "Professional-grade hard courts with flood lighting for day and night play. ITF-standard surfaces for tournament training.",
  image: "https://images.unsplash.com/photo-1712160431164-959a649036ed",
  alt: "Tennis court with professional green surface and white lines under bright lights",
  stat: "4 Courts",
  icon: "🎾"
},
{
  id: "pickleball",
  name: "Pickleball",
  tag: "India\'s Fastest Growing Sport",
  description: "Dedicated pickleball courts with professional nets and non-slip surfaces. Suitable for all skill levels from beginners to advanced players.",
  image: "https://images.unsplash.com/photo-1711996140464-3d1989f74cd8",
  alt: "Pickleball court with paddle and ball on colorful surface",
  stat: "6 Courts",
  icon: "🏓"
},
{
  id: "swimming",
  name: "Riverpool",
  tag: "Olympic Standard",
  description: "A temperature-controlled 25m pool with lane ropes, starting blocks, and underwater lighting. Coaching programs for all ages.",
  image: "https://images.unsplash.com/photo-1684586785109-fa9e989f270a",
  alt: "Olympic swimming pool with crystal clear blue water and lane dividers",
  stat: "25m Pool",
  icon: "🏊"
},
{
  id: "fitness",
  name: "Fitness Studio",
  tag: "Performance Training",
  description: "State-of-the-art equipment, functional training zones, and dedicated strength areas. Expert coaches for personalised programs.",
  image: "https://images.unsplash.com/photo-1673887910499-9007a32bfab4",
  alt: "Modern gym with weights, machines and motivational lighting",
  stat: "5000 sq ft",
  icon: "💪"
},
{
  id: "cafe",
  name: "Barebase Café",
  tag: "Fuel & Recovery",
  description: "Premium sports nutrition, cold-pressed juices, protein meals, and specialty coffee. The perfect post-session recovery destination.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18e0f29c7-1772077752824.png",
  alt: "Modern café with healthy food options and community seating area",
  stat: "All Day",
  icon: "☕"
}];


export default function FacilitiesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    // Scroll reveal
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );

    const cards = cardRefs.current;
    cards.forEach((card) => {if (card) observer.observe(card);});
    return () => cards.forEach((card) => {if (card) observer.unobserve(card);});
  }, []);

  // 3D Tilt
  const handleTilt = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const card = cardRefs.current[index];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    card.style.transform = `perspective(1000px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg) scale(1.02)`;
  };

  const handleTiltReset = (index: number) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = "perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1)";
  };

  return (
    <section
      id="facilities"
      ref={sectionRef}
      className="relative py-28 mt-0"
      style={{ background: "var(--navy-dark)" }}>
      
      {/* Background decoration */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 0%, rgba(224, 96, 49, 0.06) 0%, transparent 60%)"
        }} />
      

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">World-Class Infrastructure</p>
          <h2 className="headline-lg text-white max-w-3xl mx-auto">
            Five Disciplines,
            <br />
            <span className="gradient-text">One Destination.</span>
          </h2>
          <div className="divider-line max-w-xs mx-auto mt-8" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {facilities.map((facility, i) =>
          <div
            key={facility.id}
            ref={(el) => {cardRefs.current[i] = el;}}
            className={`reveal stagger-${Math.min(i + 1, 5)} facility-card relative h-[320px] sm:h-[380px] md:h-[420px]`}
            style={{ transition: "transform 0.4s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.4s ease, opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.${i}s" }}
            onMouseMove={(e) => handleTilt(e, i)}
            onMouseLeave={() => handleTiltReset(i)}>
            
              {/* Image */}
              <div className="absolute inset-0">
                <AppImage
                src={facility.image}
                alt={facility.alt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
              
              </div>

              {/* Overlays */}
              <div className="card-overlay" />
              <div className="card-glow" />

              {/* Content */}
              <div className="absolute inset-0 p-7 flex flex-col justify-between z-10">
                {/* Top */}
                <div className="flex items-start justify-between">
                  <span
                  className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(10px)", color: "rgba(255,255,255,0.8)" }}>
                  
                    {facility.tag}
                  </span>
                  <span className="text-2xl">{facility.icon}</span>
                </div>

                {/* Bottom */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span
                    className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{ background: "var(--terracotta)", color: "white" }}>
                    
                      {facility.stat}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">{facility.name}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{facility.description}</p>
                  <button
                  className="flex items-center gap-2 text-white font-semibold text-sm mt-2 group"
                  style={{ color: "var(--terracotta)" }}>
                  
                    Explore Facility
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}